import { useEffect, useState } from 'react';
import { Controller, FieldPath, useFormContext } from 'react-hook-form';

import { Checkbox } from '@/components/core/Checkbox';
import { Combobox } from '@/components/core/Combobox';
import { Input } from '@/components/core/Input';
import { RadioGroup, RadioGroupItem } from '@/components/core/RadioGroup';
import { Textarea } from '@/components/core/Textarea';
import { Tooltip } from '@/components/core/Tooltip';
import { mapComboboxSelectionToFormValue } from '@/components/datamodel/Enum/mapComboboxSelectionToFormValue';
import { useOptions } from '@/components/datamodel/hooks/useOptions';
import { useOptionTranslator } from '@/components/datamodel/hooks/useOptionTranslator';
import { useDataModel } from '@/hooks/useDataModel';
import { DataModelAttribute, DataModelAttributeTypeName } from '@/types/DataModel';

import {
    accountAttributeLabelId,
    type AccountProfileVariant,
    loadAccountAttributes,
} from './accountProfileVariant.ts';

const ENUM_TYPES = new Set<string>([
    DataModelAttributeTypeName.Enum,
    DataModelAttributeTypeName.EnumSet,
    DataModelAttributeTypeName.OpenEnum,
    DataModelAttributeTypeName.OpenEnumSet,
    DataModelAttributeTypeName.Codelist,
    DataModelAttributeTypeName.OpenCodelist,
    DataModelAttributeTypeName.CodelistSet,
    DataModelAttributeTypeName.OpenCodelistSet,
]);

const SET_TYPES = new Set<string>([
    DataModelAttributeTypeName.EnumSet,
    DataModelAttributeTypeName.OpenEnumSet,
    DataModelAttributeTypeName.CodelistSet,
    DataModelAttributeTypeName.OpenCodelistSet,
]);

const OPEN_TYPES = new Set<string>([
    DataModelAttributeTypeName.OpenEnum,
    DataModelAttributeTypeName.OpenEnumSet,
    DataModelAttributeTypeName.OpenCodelist,
    DataModelAttributeTypeName.OpenCodelistSet,
]);

const fieldLabelClassName = 'tw-m-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base';

interface CustomValuesRecord {
    customValues?: Record<string, unknown>;
    [key: string]: unknown;
}

interface CustomAttributeFieldsProps {
    variant: AccountProfileVariant;
    canEdit: boolean;
}

function isAttributeReadonly(attribute: DataModelAttribute, canEdit: boolean) {
    return !canEdit || Boolean(attribute.readonly || attribute.params?.readonly);
}

const AttributeLabel = ({
    attribute,
    labelId,
}: {
    attribute: DataModelAttribute;
    labelId: (name: string) => string;
}) => {
    const label = attribute.label || attribute.translatedLabel || attribute.name;
    const description = attribute.description || attribute.translatedDescription;

    return (
        <div className="tw-flex tw-items-center tw-gap-1">
            <label id={labelId(attribute.name)} htmlFor={attribute.name} className={fieldLabelClassName}>
                {label}
            </label>
            {description && (
                <span className="tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center">
                    <Tooltip popupContent={description} />
                </span>
            )}
        </div>
    );
};

const CustomEnumField = ({
    attribute,
    readonly,
    labelId,
}: {
    attribute: DataModelAttribute;
    readonly: boolean;
    labelId: (name: string) => string;
}) => {
    const { control } = useFormContext<CustomValuesRecord>();
    const { translateOption } = useOptionTranslator();
    const { options, loading } = useOptions({ attribute });
    const fieldPath = `customValues.${attribute.name}` as FieldPath<CustomValuesRecord>;
    const typeName = attribute.typeName || attribute.baseClass;
    const isSet = SET_TYPES.has(typeName);
    const isOpen = OPEN_TYPES.has(typeName);
    const mappedOptions = options.map((option) => {
        const value = option.name || option.key;
        return {
            value,
            label: option.translatedOption
                || translateOption(value, attribute, options, option.label)
                || option.value
                || value,
        };
    });

    return (
        <Controller
            name={fieldPath}
            control={control}
            render={({ field }) => {
                const selected = field.value;
                const value = isSet
                    ? mappedOptions.filter((option) => Array.isArray(selected) && selected.includes(option.value))
                    : mappedOptions.find((option) => option.value === selected) || (
                        selected ? { value: String(selected), label: String(selected) } : null
                    );

                return (
                    <Combobox
                        inputId={attribute.name}
                        aria-labelledby={labelId(attribute.name)}
                        isDisabled={readonly}
                        isLoading={loading}
                        isMulti={isSet}
                        creatable={isOpen}
                        options={mappedOptions}
                        value={value}
                        onChange={(next) => field.onChange(mapComboboxSelectionToFormValue(next, isSet))}
                    />
                );
            }}
        />
    );
};

const CustomBooleanField = ({
    attribute,
    readonly,
}: {
    attribute: DataModelAttribute;
    readonly: boolean;
}) => {
    const { control } = useFormContext<CustomValuesRecord>();
    const { translateOption } = useOptionTranslator();
    const { options } = useOptions({ attribute });
    const fieldPath = `customValues.${attribute.name}` as FieldPath<CustomValuesRecord>;

    if (options.length === 0) {
        return (
            <Controller
                name={fieldPath}
                control={control}
                render={({ field }) => (
                    <Checkbox
                        id={attribute.name}
                        isChecked={Boolean(field.value)}
                        disabled={readonly}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                    />
                )}
            />
        );
    }

    return (
        <Controller
            name={fieldPath}
            control={control}
            render={({ field }) => (
                <RadioGroup
                    id={attribute.name}
                    className="tw-flex tw-flex-row tw-flex-wrap tw-gap-4"
                    value={field.value == null ? '' : String(field.value)}
                    disabled={readonly}
                    onValueChange={field.onChange}
                >
                    {options.map((option) => {
                        const value = option.name || option.key;
                        const label = option.translatedOption
                            || translateOption(value, attribute, options, option.label)
                            || option.value
                            || value;
                        return (
                            <label key={value} className="tw-flex tw-items-center tw-gap-2 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                <RadioGroupItem id={`${attribute.name}_option_${value}`} value={value} disabled={readonly || option.disabled} />
                                <span>{label}</span>
                            </label>
                        );
                    })}
                </RadioGroup>
            )}
        />
    );
};

const CustomAttributeControl = ({
    attribute,
    canEdit,
    labelId,
}: {
    attribute: DataModelAttribute;
    canEdit: boolean;
    labelId: (name: string) => string;
}) => {
    const { register } = useFormContext<CustomValuesRecord>();
    const readonly = isAttributeReadonly(attribute, canEdit);
    const typeName = attribute.typeName || attribute.baseClass;
    const fieldPath = `customValues.${attribute.name}` as FieldPath<CustomValuesRecord>;

    let control;
    if (ENUM_TYPES.has(typeName)) {
        control = <CustomEnumField attribute={attribute} readonly={readonly} labelId={labelId} />;
    } else if (typeName === DataModelAttributeTypeName.Boolean) {
        control = <CustomBooleanField attribute={attribute} readonly={readonly} />;
    } else if (typeName === DataModelAttributeTypeName.TextArea) {
        control = (
            <Textarea
                id={attribute.name}
                rows={4}
                readOnly={readonly}
                {...register(fieldPath)}
            />
        );
    } else {
        control = (
            <Input
                id={attribute.name}
                type={typeName === DataModelAttributeTypeName.Password ? 'password' : 'text'}
                readOnly={readonly}
                {...register(fieldPath)}
            />
        );
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-1">
            <AttributeLabel attribute={attribute} labelId={labelId} />
            {control}
        </div>
    );
};

const CustomAttributeFields = ({ variant, canEdit }: CustomAttributeFieldsProps) => {
    const { dataModel } = useDataModel();
    const [attributes, setAttributes] = useState<DataModelAttribute[]>([]);
    const labelId = (name: string) => accountAttributeLabelId(variant, name);

    useEffect(() => {
        setAttributes(loadAccountAttributes(variant));
    }, [dataModel, variant]);

    return (
        <>
            {attributes.map((attribute) => (
                <CustomAttributeControl
                    key={attribute.name}
                    attribute={attribute}
                    canEdit={canEdit}
                    labelId={labelId}
                />
            ))}
        </>
    );
};

export { CustomAttributeFields };
export type { CustomValuesRecord };
